"use client";

import { useState } from "react";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";

const LETTER_THRESHOLDS: { min: number; letter: string }[] = [
  { min: 93, letter: "A" },
  { min: 90, letter: "A-" },
  { min: 87, letter: "B+" },
  { min: 83, letter: "B" },
  { min: 80, letter: "B-" },
  { min: 77, letter: "C+" },
  { min: 73, letter: "C" },
  { min: 70, letter: "C-" },
  { min: 67, letter: "D+" },
  { min: 63, letter: "D" },
  { min: 60, letter: "D-" },
];

function letterForPercent(percent: number): string {
  for (const threshold of LETTER_THRESHOLDS) {
    if (percent >= threshold.min) {
      return threshold.letter;
    }
  }
  return "F";
}

interface CategoryRow {
  id: number;
  name: string;
  weight: string;
  earned: string;
  possible: string;
}

let nextRowId = 0;

function makeRow(name: string, weight: string): CategoryRow {
  nextRowId += 1;
  return { id: nextRowId, name, weight, earned: "", possible: "100" };
}

interface GradeResult {
  percent: number;
  weightSum: number;
  normalized: boolean;
}

function computeGrade(rows: CategoryRow[]): GradeResult | null {
  let weightSum = 0;
  let weighted = 0;
  for (const row of rows) {
    const weight = parseNumberInput(row.weight);
    const earned = parseNumberInput(row.earned);
    const possible = parseNumberInput(row.possible);
    if (
      weight !== null &&
      weight > 0 &&
      earned !== null &&
      possible !== null &&
      possible > 0
    ) {
      weightSum += weight;
      weighted += weight * (earned / possible) * 100;
    }
  }
  if (weightSum <= 0) {
    return null;
  }
  return {
    percent: weighted / weightSum,
    weightSum,
    normalized: Math.abs(weightSum - 100) > 0.001,
  };
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const GRADE_CALCULATOR_STRINGS = {
  gradeCategories: "Grade Categories",
  category: "Category",
  categoryPlaceholder: "Category {number}",
  weightPercent: "Weight %",
  score: "Score",
  outOf: "Out of",
  removeCategoryAria: "Remove category {number}",
  remove: "Remove",
  addCategory: "Add Category",
  currentGrade: "Current grade",
  letterGrade: "Letter grade",
  weightsEntered: "Weights entered",
  normalizedNote:
    "Your weights sum to {sum}%, not 100%, so the grade shown is normalized — each category counts in proportion to the weight you entered.",
  letterGradeScale: "Letter Grade Scale",
  letterGradeHeader: "Letter grade",
  percentageHeader: "Percentage",
  andAbove: "{min}% and above",
  fLetter: "F",
  belowSixtyPercent: "Below 60%",
  sampleHomework: "Homework",
  sampleQuizzes: "Quizzes",
  sampleMidterm: "Midterm",
  sampleFinalExam: "Final Exam",
} as const;

export default function GradeCalculatorWidget() {
  const strings = useToolUi(GRADE_CALCULATOR_STRINGS);
  const [rows, setRows] = useState<CategoryRow[]>(() => [
    makeRow(strings.sampleHomework, "20"),
    makeRow(strings.sampleQuizzes, "20"),
    makeRow(strings.sampleMidterm, "25"),
    makeRow(strings.sampleFinalExam, "35"),
  ]);

  const fmt = useToolNumber();

  const result = computeGrade(rows);

  function updateRow(id: number, patch: Partial<CategoryRow>) {
    setRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, ...patch } : row))
    );
  }

  function addRow() {
    setRows((prev) => [...prev, makeRow("", "")]);
  }

  function removeRow(id: number) {
    setRows((prev) => prev.filter((row) => row.id !== id));
  }

  return (
    <>
      {/* Category rows */}
      <section className="space-y-3">
        <h2 className="font-semibold text-foreground text-lg">
          {strings.gradeCategories}
        </h2>
        <div className="space-y-3">
          {rows.map((row, index) => (
            <div
              className="flex flex-wrap items-end gap-3 rounded-lg border border-border bg-card p-4"
              key={row.id}
            >
              <div className="min-w-0 basis-full space-y-1 sm:flex-1 sm:basis-auto">
                <label
                  className="block font-medium text-foreground text-xs"
                  htmlFor={`cat-name-${row.id}`}
                >
                  {strings.category}
                </label>
                <input
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  id={`cat-name-${row.id}`}
                  onChange={(e) => updateRow(row.id, { name: e.target.value })}
                  placeholder={t(strings.categoryPlaceholder, {
                    number: index + 1,
                  })}
                  type="text"
                  value={row.name}
                />
              </div>
              <div className="space-y-1">
                <label
                  className="block font-medium text-foreground text-xs"
                  htmlFor={`cat-weight-${row.id}`}
                >
                  {strings.weightPercent}
                </label>
                <input
                  className="w-24 rounded-lg border border-border bg-background px-3 py-2 text-center text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  id={`cat-weight-${row.id}`}
                  min={0}
                  onChange={(e) =>
                    updateRow(row.id, { weight: e.target.value })
                  }
                  step="0.1"
                  type="number"
                  value={row.weight}
                />
              </div>
              <div className="space-y-1">
                <label
                  className="block font-medium text-foreground text-xs"
                  htmlFor={`cat-earned-${row.id}`}
                >
                  {strings.score}
                </label>
                <input
                  className="w-24 rounded-lg border border-border bg-background px-3 py-2 text-center text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  id={`cat-earned-${row.id}`}
                  min={0}
                  onChange={(e) =>
                    updateRow(row.id, { earned: e.target.value })
                  }
                  step="0.1"
                  type="number"
                  value={row.earned}
                />
              </div>
              <div className="space-y-1">
                <label
                  className="block font-medium text-foreground text-xs"
                  htmlFor={`cat-possible-${row.id}`}
                >
                  {strings.outOf}
                </label>
                <input
                  className="w-24 rounded-lg border border-border bg-background px-3 py-2 text-center text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  id={`cat-possible-${row.id}`}
                  min={0}
                  onChange={(e) =>
                    updateRow(row.id, { possible: e.target.value })
                  }
                  step="0.1"
                  type="number"
                  value={row.possible}
                />
              </div>
              <button
                aria-label={t(strings.removeCategoryAria, {
                  number: index + 1,
                })}
                className="rounded-lg border border-border bg-card px-3 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
                disabled={rows.length <= 1}
                onClick={() => removeRow(row.id)}
                type="button"
              >
                {strings.remove}
              </button>
            </div>
          ))}
        </div>
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90"
          onClick={addRow}
          type="button"
        >
          {strings.addCategory}
        </button>
      </section>

      {/* Result */}
      {result && (
        <section className="space-y-3">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <StatCard
              label={strings.currentGrade}
              value={`${fmt(result.percent, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`}
            />
            <StatCard
              label={strings.letterGrade}
              value={letterForPercent(result.percent)}
            />
            <StatCard
              label={strings.weightsEntered}
              value={`${result.weightSum}%`}
            />
          </div>
          {result.normalized && (
            <p className="rounded-lg border border-border bg-muted/30 p-4 text-muted-foreground text-sm leading-relaxed">
              {t(strings.normalizedNote, { sum: result.weightSum })}
            </p>
          )}
        </section>
      )}

      {/* Letter scale reference */}
      <section className="space-y-3 rounded-lg border border-border bg-muted/30 p-6">
        <h2 className="font-semibold text-foreground text-lg">
          {strings.letterGradeScale}
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-border border-b">
                <th className="py-2 pr-4 font-medium text-foreground">
                  {strings.letterGradeHeader}
                </th>
                <th className="py-2 font-medium text-foreground">
                  {strings.percentageHeader}
                </th>
              </tr>
            </thead>
            <tbody className="text-muted-foreground">
              {LETTER_THRESHOLDS.map((threshold) => (
                <tr
                  className="border-border/50 border-b"
                  key={threshold.letter}
                >
                  <td className="py-2 pr-4 font-medium text-foreground">
                    {threshold.letter}
                  </td>
                  <td className="py-2">
                    {t(strings.andAbove, { min: threshold.min })}
                  </td>
                </tr>
              ))}
              <tr>
                <td className="py-2 pr-4 font-medium text-foreground">
                  {strings.fLetter}
                </td>
                <td className="py-2">{strings.belowSixtyPercent}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}
